
import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import ToolsDropdown from '@/components/ToolsDropdown';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Memoize the scroll handler to improve performance
  const handleScroll = useCallback(() => {
    if (window.scrollY > 10) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
      return;
    }

    // Fallback: if section not found on current page, navigate to home
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    }
  };

  return (
    <nav 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled ? "bg-white shadow-md py-2" : "bg-transparent py-4"
      )}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/" className="hidden md:block">
            <img 
              src="/lovable-uploads/9660a1c7-e321-4122-b380-58c44ed97180.png" 
              alt="AD Email Lab Logo" 
              className="h-12"
              width="48"
              height="48"
              loading="eager"
            />
          </Link>
          <ToolsDropdown />
        </div>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center space-x-8">
          <Link
            to="/"
            className="text-secondary hover:text-primary font-medium transition-colors"
          >
            Home
          </Link>
          <Link
            to="/about"
            className="text-secondary hover:text-primary font-medium transition-colors"
          >
            About Me
          </Link>
          <Button
            onClick={() => scrollToSection('contact')}
            variant="default"
            className="bg-primary hover:bg-primary/90 text-white"
          >
            Let's Talk
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <button
            onClick={toggleMobileMenu}
            className="text-secondary hover:text-primary p-2"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-md animate-fade-in">
          <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <Link
              to="/"
              className="text-secondary hover:text-primary py-2 font-medium transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-secondary hover:text-primary py-2 font-medium transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              About Me
            </Link>
            <Button
              onClick={() => scrollToSection('contact')}
              variant="default"
              className="bg-primary hover:bg-primary/90 text-white w-full"
            >
              Let's Talk
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
